import React, { useState, useEffect, useCallback, startTransition } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../../layout/DesktopView";
import { useToast } from "../../common/ToastContext";
import PresentationCard from "./PresentationCard";
import presentationService from "../../../services/presentationService";
import PresentationEditor from "./PresentationEditor";

const PresentationDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const [presentations, setPresentations] = useState([]);
  const [selectedPresentation, setSelectedPresentation] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const fetched = await presentationService.list();
      setPresentations(fetched);
    } catch (error) {
      console.error("Error fetching:", error);
      showToast("Failed to fetch presentations", "error");
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.has("id") && !selectedPresentation) {
      const editId = searchParams.get("id");
      const editObj = presentations.find((obj) => obj.id === editId);
      if (editObj) {
        setSelectedPresentation(editObj);
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [searchParams, presentations, selectedPresentation, setSearchParams]);

  const handleSave = async (data) => {
    try {
      const previewing = data?.previewing === null ? false : data.previewing;
      if (data?.previewing) {
        delete data.previewing;
      }
      await presentationService.update({ id: data.id, value: data });
      await fetchData();
      if (!previewing) {
        handleCloseEditor();
        showToast("Presentation saved successfully", "info");
      }
    } catch (error) {
      console.error("Error saving:", error);
      showToast(`Error saving presentation: ${error}`, "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await presentationService.delete(id);
      await fetchData();
      handleCloseEditor();
      showToast("Presentation deleted successfully", "info");
    } catch (error) {
      console.error("Error deleting:", error);
      showToast(`Error deleting presentation: ${error}`, "error");
    }
  };

  const handleCloseEditor = () => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the editor.
    startTransition(() => setSelectedPresentation(null));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  };

  const handleAddNew = async () => {
    const randFourDigits = Math.floor(1000 + Math.random() * 9000);
    const newPresentation = {
      key: `presentation-${randFourDigits}`,
      value: { name: `New Presention ${randFourDigits}` },
    };
    try {
      const created = await presentationService.create(newPresentation);
      await fetchData();
      setSelectedPresentation(created);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("id", created.id);
        return newParams;
      });
      showToast("New presentation created successfully", "info");
    } catch (error) {
      console.error("Error creating new:", error);
      showToast(`Error creating new presentation: ${error}`, "error");
    }
  };

  const handleClick = (item) => {
    setSelectedPresentation(item);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("id", item.id);
      return newParams;
    });
  };

  const handleSearch = (term) => {
    // This function is left empty as search is handled internally by DesktopView
  };

  return (
    <DesktopView
      items={presentations}
      selectedItem={selectedPresentation}
      onSearch={handleSearch}
      searchPlaceholder="Filter presentations ..."
      contextMenuItems={[]}
      onItemClick={handleClick}
      onSave={handleSave}
      onDelete={handleDelete}
      onReset={() => {}}
      onClose={handleCloseEditor}
      Card={PresentationCard}
      Editor={PresentationEditor}
      addNewItem={handleAddNew}
      eventName="presentationCreated"
      hideOnSelect={false}
    />
  );
};

export default PresentationDesktop;
